import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type { IViTri, IViTriImportRow, IViTriPayload, TrangThaiViTri } from '@/interfaces/viTriInterfaces'

const PREFIX = '/vi-tri'

function chiNhanhFromRaw(raw: Record<string, unknown>): IViTri['chiNhanh'] {
  const nested = raw.chiNhanh
  if (!nested || typeof nested !== 'object') return null
  const o = nested as Record<string, unknown>
  const ten = o.ten != null ? String(o.ten).trim() : ''
  const ma = o.ma != null ? String(o.ma).trim() : ''
  const id = o.id != null && Number.isFinite(Number(o.id)) ? Number(o.id) : undefined
  if (!ten && !ma && id == null) return null
  return { id, ma: ma || undefined, ten: ten || undefined }
}

function mapViTriFromApi(raw: Record<string, unknown>): IViTri {
  const trangThai = raw.trangThai
  const chiNhanh = chiNhanhFromRaw(raw)
  const tenTrucTiep = raw.tenChiNhanh != null ? String(raw.tenChiNhanh).trim() : ''
  const chiNhanhIdRaw = raw.chiNhanhId != null ? Number(raw.chiNhanhId) : chiNhanh?.id
  return {
    id: String(raw.id ?? ''),
    maViTri: String(raw.maViTri ?? ''),
    tenViTri: String(raw.tenViTri ?? ''),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai:
      trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong' ? trangThai : 'dangHoatDong',
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
    chiNhanhId: chiNhanhIdRaw != null && Number.isFinite(chiNhanhIdRaw) ? chiNhanhIdRaw : null,
    tenChiNhanh: chiNhanh?.ten || tenTrucTiep || null,
    chiNhanh,
  }
}

function toPayload(body: IViTriPayload) {
  const payload: Record<string, unknown> = { tenViTri: body.tenViTri.trim() }
  if (body.maViTri?.trim()) payload.maViTri = body.maViTri.trim()
  if (body.ghiChu?.trim()) payload.ghiChu = body.ghiChu.trim()
  return payload
}

export function viTriListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const maViTri = q.maViTri
  if (typeof maViTri === 'string' && maViTri.trim()) out.maViTri = maViTri.trim()
  const tenViTri = q.tenViTri
  if (typeof tenViTri === 'string' && tenViTri.trim()) out.tenViTri = tenViTri.trim()
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong') out.trangThai = trangThai
  const chiNhanhRaw = q.chiNhanhId
  const chiNhanhParts = Array.isArray(chiNhanhRaw)
    ? chiNhanhRaw.flatMap((item) => String(item).split(','))
    : typeof chiNhanhRaw === 'string'
      ? chiNhanhRaw.split(',')
      : []
  const chiNhanhIds = Array.from(
    new Set(chiNhanhParts.map((item) => item.trim()).filter((item) => Number(item) > 0)),
  )
  if (chiNhanhIds.length > 0) out.chiNhanhId = chiNhanhIds.join(',')
  return out
}

export function fetchDanhSachViTri(search: string): Promise<PagedListResult<IViTri>> {
  const params = viTriListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-vi-tri`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: (data as Record<string, unknown>[]).map(mapViTriFromApi),
        paging,
      }
    }),
  )
}

export function fetchChiTietViTri(id: string | number): Promise<IViTri> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-vi-tri`).then((res: unknown) =>
      mapViTriFromApi(normalizeDetailResponse(res) as Record<string, unknown>),
    ),
  )
}

export function taoViTri(
  body: IViTriPayload,
): Promise<{ data: IViTri }> {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-vi-tri`, toPayload(body)),
  ).then((res: unknown) => {
    // Backend trả `{ statusCode, message, data: IViTri }` qua TransformInterceptor.
    // Tự động unwrap để caller nhận `entity` trực tiếp.
    const wrapped = res as { data?: Record<string, unknown> } | Record<string, unknown>
    const raw = (wrapped as { data?: Record<string, unknown> }).data ?? (wrapped as Record<string, unknown>)
    return { data: mapViTriFromApi(raw) }
  })
}

export function capNhatViTri(id: string | number, body: IViTriPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-vi-tri`, toPayload(body)))
}

export function xoaViTri(id: string | number) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-vi-tri`))
}

export type XoaNhieuViTriResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều vị trí — gọi API xóa từng id (loại trùng id). */
export async function xoaNhieuViTri(ids: string[]): Promise<XoaNhieuViTriResult> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)))
  const failed: XoaNhieuViTriResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaViTri(id)
      ok += 1
    } catch (e) {
      failed.push({
        id,
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiViTri(id: string | number, trangThai: TrangThaiViTri) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-vi-tri`, { trangThai }))
}

export type ViTriImportResult = {
  success: boolean
  message: string
  created?: number
  updated?: number
  data?: unknown[]
}

export function nhapDanhSachViTri(rows: IViTriImportRow[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/vi-tri-nhap-danh-sach`, rows))
}
